"use client";
import React, { useRef, useEffect } from "react";
import Image from "next/image";

/* ─── Ripple helper ─── */
function createRipple(e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) {
  const btn = e.currentTarget;
  const existing = btn.querySelector(".ripple-wave");
  if (existing) existing.remove();

  const rect = btn.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height) * 2;
  const x = e.clientX - rect.left - size / 2;
  const y = e.clientY - rect.top - size / 2;

  const ripple = document.createElement("span");
  ripple.className = "ripple-wave";
  ripple.style.cssText = `
    position:absolute;width:${size}px;height:${size}px;
    left:${x}px;top:${y}px;
    border-radius:50%;pointer-events:none;
    background:rgba(255,255,255,0.25);
    transform:scale(0);opacity:1;
    animation:ripple-expand 0.55s cubic-bezier(0.4,0,0.2,1) forwards;
  `;
  btn.style.position = "relative";
  btn.style.overflow = "hidden";
  btn.appendChild(ripple);
  ripple.addEventListener("animationend", () => ripple.remove());
}

const HeroSection: React.FC = () => {
  const blobRef1 = useRef<HTMLDivElement>(null);
  const blobRef2 = useRef<HTMLDivElement>(null);
  const blobRef3 = useRef<HTMLDivElement>(null);
  const heroContentRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    /* Entrance stagger */
    const elements = [
      { el: badgeRef.current, delay: 200 },
      { el: headlineRef.current, delay: 400 },
      { el: descRef.current, delay: 600 },
      { el: ctaRef.current, delay: 800 },
    ];
    elements.forEach(({ el, delay }) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(30px)";
      el.style.transition =
        "opacity 1.2s cubic-bezier(0.16,1,0.3,1), transform 1.2s cubic-bezier(0.16,1,0.3,1)";
      setTimeout(() => {
        if (!el) return;
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
      }, delay);
    });

    /* Cursor-reactive parallax */
    const handleMouseMove = (e: MouseEvent) => {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      const dx = (e.clientX - cx) / cx;
      const dy = (e.clientY - cy) / cy;

      if (blobRef1.current)
        blobRef1.current.style.transform = `translate(${dx * 80}px, ${dy * 80}px)`;
      if (blobRef2.current)
        blobRef2.current.style.transform = `translate(${-dx * 60}px, ${-dy * 60}px)`;
      if (blobRef3.current)
        blobRef3.current.style.transform = `translate(${dx * 40}px, ${dy * -40}px)`;
      if (heroContentRef.current)
        heroContentRef.current.style.transform = `perspective(1000px) rotateY(${dx * 2}deg) rotateX(${-dy * 2}deg)`;
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col justify-center items-center px-6 pt-28 pb-20 overflow-hidden"
    >
      {/* Background blobs */}
      <div
        ref={blobRef1}
        className="absolute top-[15%] left-[10%] w-[45vw] h-[45vw] rounded-full parallax-blob"
        style={{ background: "radial-gradient(circle,#dfc08a,transparent)", filter: "blur(60px)", opacity: 0.4 }}
      />
      <div
        ref={blobRef2}
        className="absolute bottom-[20%] right-[8%] w-[40vw] h-[40vw] rounded-full parallax-blob"
        style={{ background: "radial-gradient(circle,#d4c8e8,transparent)", filter: "blur(70px)", opacity: 0.3 }}
      />
      <div
        ref={blobRef3}
        className="absolute top-1/2 left-1/2 w-[60vw] h-[60vw] -translate-x-1/2 -translate-y-1/2 rounded-full parallax-blob"
        style={{ background: "radial-gradient(circle,#c8a96a,transparent)", filter: "blur(90px)", opacity: 0.15 }}
      />

      {/* Content */}
      <div ref={heroContentRef} className="relative max-w-5xl mx-auto flex flex-col items-center text-center gap-6 md:gap-8">
        {/* Badge */}
        <div ref={badgeRef} className="px-5 py-2 rounded-full glass border border-accent/20 shadow-soft">
          <span className="section-label">Tarot · Numerology · Healing · Mindset</span>
        </div>

        {/* Title */}
        <div className="flex flex-col items-center gap-2">
          <p className="text-foreground-muted text-sm tracking-[0.2em] uppercase font-body">
            TarotArtist by Arpita Kasar
          </p>
          <div className="flex flex-col items-center text-accent/90">
            <p className="text-xs md:text-sm tracking-widest uppercase font-semibold font-body">
              Trusted by 50k+ clients
            </p>
            <p className="text-[10px] md:text-xs tracking-widest uppercase font-medium font-body mt-0.5 opacity-80">
              since 2016
            </p>
          </div>
        </div>

        {/* Headline */}
        <h1
          ref={headlineRef}
          className="font-display text-foreground text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight"
        >
          Rewire Your <br />
          <span className="shimmer-gold italic">Subconscious.</span>
        </h1>

        {/* Subline */}
        <p ref={descRef} className="text-foreground-muted max-w-lg text-base md:text-lg font-body">
          Align Your Energy. Transform Your Reality.
        </p>

        {/* CTA */}
        <div ref={ctaRef} className="flex flex-col sm:flex-row gap-4 mt-2">
          <a
            href="https://wa.me/919993297909?text=Hi%2C%20I%20want%20to%20book%20a%20tarot%20reading%20session."
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold px-8 py-3.5 rounded-full text-sm md:text-base"
            onMouseDown={createRipple}
          >
            Book Your Reading
          </a>
          <a href="#services" className="btn-outline-gold px-8 py-3.5 rounded-full text-sm md:text-base">
            Explore
          </a>
        </div>

        {/* Hero Card */}
        <div className="mt-8 w-[220px] sm:w-[280px] md:w-[300px] aspect-[2/3] rounded-3xl overflow-hidden shadow-gold-lg relative">
          <Image
            src="/assets/images/card.jpg"
            alt="The Artist XXIII — Tarot Card"
            fill
            className="object-cover"
            sizes="300px"
            priority
          />
        </div>
      </div>
    </section>
  );
};

export { createRipple };
export default HeroSection;
